import { Button } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { ProductType } from '@vy/services';
import { getCurrencySymbol, isRevShareProductVariant, isTiersVolumeProductVariant, PRODUCT_TYPES } from '@vy/services';
import type {
  Currency,
  NewUsageProduct,
  ProductVariantInput,
  UsageProduct,
  UsageProductInput,
  UsageProductVariant,
} from '@vy/types';
import { MAX_TO, PRICING_MODELS } from '@vy/types';
import { formatNumber, isNotNullOrUndefined, newId, raise } from '@vy/utils';
import cn from 'classnames';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDialog } from '../../../../../../hooks/use-dialog';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { ModifyButton } from '../../ModifyButton';
import { UsageFeesModifyPanel } from '../../UsageFeesModifyPanel';

type UsageProductVariantSummaryProps = {
  product: UsageProduct | NewUsageProduct;
  productIndex: number;
  editable: boolean;
  isGroupedProduct?: boolean;
  currency?: Currency;
};

export const UsageProductVariantSummary: React.FC<UsageProductVariantSummaryProps> = ({
  product,
  productIndex,
  editable,
  isGroupedProduct,
  currency,
}) => {
  const { isDialogOpen: isDrawerOpen, handleOpen, handleClose } = useDialog();
  const { setValue, getValues } = useFormContext<PlanInputForm, unknown, NewPlanForm>();

  const onAddVariant = () => {
    const newVariant = getDefaultProductVariant(PRODUCT_TYPES.USAGE, product, isFlatRateEnabled);
    const updatedProductVariants = [...product.variants, newVariant] as UsageProductVariant[];
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);
  };

  const usageVariants = useMemo(
    () => getValues(`usageProducts.${productIndex}.variants`) ?? [],
    [getValues, productIndex],
  );

  const isFlatRateEnabled = useMemo(() => {
    return usageVariants.some((usageVariant) => {
      switch (usageVariant.pricing.pricingModel) {
        case PRICING_MODELS.PerUnit:
          return false;
        case PRICING_MODELS.Tiers:
          return usageVariant.pricing.priceConfiguration?.isFlatRate ?? false;
        default:
          return false;
      }
    });
  }, [usageVariants]);

  const addSubProductVisible = editable && isGroupedProduct;

  return (
    <>
      {addSubProductVisible && (
        <div
          className={cn('px-8 pb-4 pt-2.5', {
            'bg-product-bk': product.variants?.length % 2 === 0,
          })}
        >
          <Button onClick={onAddVariant} className="gap-x-2" disabled={!addSubProductVisible}>
            <VyIcon icon="PlusMedium" />
            Add Product Variant
          </Button>
        </div>
      )}
      <div
        className={cn(
          'flex items-center justify-between px-6',
          { 'border-t border-t-line py-3': editable },
          { 'py-5': !editable },
        )}
      >
        {editable ? (
          <div className="flex items-center gap-2">
            <ModifyButton isDialogOpen={isDrawerOpen} handleOpen={handleOpen} />

            <UsageFeesModifyPanel
              open={isDrawerOpen}
              onClose={handleClose}
              usageProduct={product as UsageProductInput}
            />
          </div>
        ) : (
          <div className="flex space-x-4">
            {isNotNullOrUndefined(product.basePrice) ? (
              <div className="flex items-center space-x-2">
                <label className="text-label">Base Amount</label>
                <p className="text-xl font-semibold">{`$${formatNumber(product.basePrice)}`}</p>
              </div>
            ) : (
              <></>
            )}
            {isNotNullOrUndefined(product.commitment) && isNotNullOrUndefined(product.commitment.amount) ? (
              <div className="flex items-center space-x-2">
                <label className="text-label">Commitment</label>
                <p className="text-xl font-semibold">{`${formatNumber(product.commitment.amount)} ${product.commitment.type?.toLowerCase()}`}</p>
              </div>
            ) : (
              <></>
            )}
            {isNotNullOrUndefined(product.currencyCommitment) ? (
              <div className="flex items-center space-x-2">
                <label className="text-label">Commitment</label>
                <p className="text-xl font-semibold">{`${getCurrencySymbol(currency)} ${formatNumber(product.currencyCommitment)}`}</p>
              </div>
            ) : (
              <></>
            )}
            {isNotNullOrUndefined(product.rateMultiplier) && (
              <div className="flex items-center space-x-2">
                <label className="text-label">Rate Multiplier</label>
                <p className="text-xl font-semibold">{formatNumber(product.rateMultiplier)}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const getDefaultProductVariant = (
  productType: ProductType,
  product: UsageProduct | NewUsageProduct,
  isFlatRateEnabled: boolean,
): ProductVariantInput => {
  const newProductVariant = { id: newId() };
  const isVolume = isTiersVolumeProductVariant(product.variants[0]);
  const isRevShare = isRevShareProductVariant(product.variants[0]);

  switch (productType) {
    case PRODUCT_TYPES.FIXED:
      return {
        ...newProductVariant,
        pricing: {
          pricingModel: PRICING_MODELS.FixedOneTime,
        },
      };
    case PRODUCT_TYPES.USAGE:
      return {
        ...newProductVariant,
        pricing: {
          pricingModel: PRICING_MODELS.Tiers,
          priceConfiguration: {
            isFlatRate: isFlatRateEnabled,
            isVolume,
            isRevShare,
            tiers: [{ from: 0, to: MAX_TO }],
          },
        },
      };
    default:
      return raise(`Unknown product type: ${productType}`);
  }
};
